import { useEffect, useRef } from 'react';

interface HeroProps {
  onGetQuote: () => void;
}

export default function Hero({ onGetQuote }: HeroProps) {
  const statsRef = useRef<HTMLDivElement>(null);
  const mobileStatsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const counters = entry.target.querySelectorAll('.counter');
            counters.forEach((counter) => {
              const target = parseInt(counter.getAttribute('data-target') || '0');
              animateCounter(counter as HTMLElement, target);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }
    
    if (mobileStatsRef.current) {
      observer.observe(mobileStatsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const animateCounter = (element: HTMLElement, target: number) => {
    let current = 0;
    const increment = target / 50;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        element.textContent = target.toString();
        clearInterval(timer);
      } else {
        element.textContent = Math.floor(current).toString();
      }
    }, 30);
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/919711165605?text=Hi, I need a bulk quote for printer consumables', '_blank');
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-0 w-full">
      {/* Modern Gradient Background with Mesh */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-teal-900 to-slate-950"></div>
      
      {/* Product-themed Background Image Overlay */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/a66e5a0128bb09721bd12b0dae93b13b.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      ></div>

      {/* Animated Gradient Mesh Overlay */}
      <div className="absolute inset-0 opacity-30 overflow-hidden">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-teal-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
        <div className="absolute top-0 -right-4 w-96 h-96 bg-orange-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-96 h-96 bg-teal-600 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
      }}></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-12 py-2 sm:py-4 md:py-8 lg:py-12 relative z-10 w-full max-w-full">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center w-full">
          {/* Left Content */}
          <div className="space-y-4 sm:space-y-5 lg:space-y-6 animate-fade-in-up w-full max-w-full flex flex-col items-center lg:items-start text-center lg:text-left">
            {/* Modern Badge with Glow */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-md border border-white/20 rounded-full shadow-lg hover:bg-white/15 transition-all duration-300 mt-0">
              <span className="text-xl sm:text-2xl">🇮🇳</span>
              <span className="text-xs sm:text-sm font-semibold text-white">Trusted B2B Supplier Since 2011</span>
              <div className="w-2 h-2 rounded-full bg-teal-400 animate-pulse"></div>
            </div>

            {/* Headline with Modern Typography */}
            <div className="space-y-2 lg:space-y-3 w-full">
              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white leading-[1.1] tracking-tight break-words">
                Premium B2B
                <br />
                <span className="bg-gradient-to-r from-teal-400 via-teal-300 to-orange-400 bg-clip-text text-transparent">
                  Printer Solutions
                </span>
              </h1>
              <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-teal-300 mt-2">
                Consumables • Lamination • Equipment
              </p>
            </div>

            {/* Value Proposition */}
            <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-xl leading-relaxed break-words pt-2">
              Empowering retailers, wholesalers, and service centers across India with <strong className="text-white">bulk pricing</strong> and <strong className="text-white">guaranteed stock availability</strong>.
            </p>

            {/* Modern CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 w-full justify-center lg:justify-start">
              <button
                onClick={onGetQuote}
                className="group relative px-8 py-4 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-400 hover:to-teal-500 text-white text-base font-bold rounded-xl shadow-2xl hover:shadow-teal-500/50 transition-all duration-300 flex items-center justify-center gap-3 overflow-hidden whitespace-nowrap cursor-pointer w-full sm:w-auto"
              >
                <span className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></span>
                <span className="relative flex items-center gap-3">
                  <i className="ri-file-text-line text-xl"></i>
                  Get Bulk Quote
                  <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
                </span>
              </button>
              
              <button
                onClick={handleWhatsApp}
                className="group px-8 py-4 bg-white/10 backdrop-blur-md border-2 border-white/30 text-white hover:bg-white hover:text-teal-900 text-base font-bold rounded-xl shadow-xl transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer w-full sm:w-auto"
              >
                <i className="ri-whatsapp-line text-2xl"></i>
                WhatsApp Now
                <i className="ri-arrow-right-up-line text-lg group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"></i>
              </button>
            </div>

            {/* Modern Stats Grid - Mobile */}
            <div 
              ref={mobileStatsRef}
              className="grid grid-cols-4 gap-2 pt-6 lg:hidden w-full"
            >
              {[
                { number: '500', suffix: '+', label: 'Partners', icon: 'ri-team-line' },
                { number: '9', suffix: '', label: 'Products', icon: 'ri-stack-line' },
                { number: '28', suffix: '', label: 'States', icon: 'ri-map-pin-line' },
                { number: '24', suffix: 'hr', label: 'Support', icon: 'ri-customer-service-2-line' },
              ].map((stat, index) => (
                <div key={index} className="flex flex-col items-center justify-center space-y-1 bg-white/5 backdrop-blur-sm rounded-lg p-2 border border-white/10 w-full min-w-0">
                  <i className={`${stat.icon} text-xl text-teal-400 flex-shrink-0`}></i>
                  <div className="text-lg font-black text-white whitespace-nowrap flex-shrink-0">
                    <span className="counter" data-target={stat.number}>0</span>
                    <span>{stat.suffix}</span>
                  </div>
                  <p className="text-[9px] text-gray-400 font-medium text-center whitespace-nowrap flex-shrink-0">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right Visual - 3D Product Showcase */}
          <div className="hidden lg:block relative h-[600px] animate-fade-in">
            {/* Floating Product Cards */}
            <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
              <div className="relative w-full h-full perspective-1000">
                {/* Center - Main Product Card */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 animate-float">
                  <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-2xl hover:scale-105 transition-transform duration-500">
                    <div className="w-full h-64 mb-4">
                      <img
                        src="https://readdy.ai/api/search-image?query=professional%20black%20laser%20toner%20cartridge%20premium%20product%20photography%20on%20transparent%20background%20with%20soft%20studio%20lighting%20high%20quality%20detailed%20view%20modern%20aesthetic&width=400&height=400&seq=hero_main1&orientation=squarish"
                        alt="Premium Toner"
                        className="w-full h-full object-contain drop-shadow-2xl"
                      />
                    </div>
                    <div className="text-center">
                      <h3 className="text-white font-bold text-lg mb-1">Compatible Toner Cartridges</h3>
                      <p className="text-teal-300 text-sm">Premium Quality • Best Prices</p>
                    </div>
                  </div>
                </div>

                {/* Floating Cards Around */}
                {[
                  { 
                    image: 'lamination pouch sheets professional product photography transparent background studio lighting premium quality modern aesthetic',
                    title: 'Lamination Pouches',
                    position: 'top-12 right-4',
                    rotate: 'rotate-6',
                    size: 'w-64',
                    seq: 'hero_float1',
                    staticUrl: 'https://static.readdy.ai/image/a63ec0b6f34fb9b66d50256c348ce6a2/d826d2a1bb6070c330d63374975d3327.png'
                  },
                  { 
                    image: 'OPC drum cylinder printer component professional product photography transparent background studio lighting premium quality modern aesthetic',
                    title: 'Laminator',
                    position: 'bottom-16 left-4',
                    rotate: '-rotate-6',
                    size: 'w-56',
                    seq: 'hero_float2',
                    staticUrl: 'https://static.readdy.ai/image/a63ec0b6f34fb9b66d50256c348ce6a2/0eef544e251c6914d5b4277d21fe7198.png'
                  },
                  { 
                    image: 'toner powder bottle refill professional product photography transparent background studio lighting premium quality modern aesthetic',
                    title: 'Gi2 Premium Ink',
                    position: 'top-32 left-8',
                    rotate: 'rotate-12',
                    size: 'w-48',
                    seq: 'hero_float3',
                    staticUrl: 'https://static.readdy.ai/image/a63ec0b6f34fb9b66d50256c348ce6a2/aad51b3345c04a6d3d592cb35836dc12.png'
                  },
                ].map((item, index) => (
                  <div 
                    key={index}
                    className={`absolute ${item.position} ${item.size} ${item.rotate} animate-float-delayed opacity-80 hover:opacity-100 transition-opacity duration-300`}
                  >
                    <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-4 shadow-xl">
                      <div className="w-full h-32 mb-2">
                        <img
                          src={item.staticUrl}
                          alt={item.title}
                          className="w-full h-full object-contain drop-shadow-lg"
                        />
                      </div>
                      <p className="text-white text-xs font-semibold text-center">{item.title}</p>
                    </div>
                  </div>
                ))}

              </div>

              {/* Radial Glow Effect */}
              <div className="absolute inset-0 bg-gradient-radial from-teal-500/20 via-transparent to-transparent pointer-events-none"></div>
            </div>
          </div>
        </div>

        {/* Modern Trust Indicators - Desktop */}
        <div
          ref={statsRef}
          className="hidden lg:flex justify-center items-center gap-6 mt-12 w-full"
        >
          <div className="grid grid-cols-4 gap-6 max-w-6xl">
          {[
            { number: 500, suffix: '+', label: 'Trade Partners', icon: 'ri-team-line', color: 'from-teal-400 to-teal-600' },
            { number: 9, suffix: '', label: 'Product Categories', icon: 'ri-stack-line', color: 'from-orange-400 to-orange-600' },
            { number: 28, suffix: '', label: 'States Covered', icon: 'ri-map-pin-line', color: 'from-teal-400 to-teal-600' },
            { number: 24, suffix: 'hr', label: 'Quote Response', icon: 'ri-customer-service-2-line', color: 'from-orange-400 to-orange-600' },
          ].map((stat, index) => (
            <div 
              key={index} 
              className="group text-center space-y-4 animate-fade-in-up hover:scale-105 transition-transform duration-300 bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 flex flex-col items-center justify-center" 
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg group-hover:shadow-2xl transition-all duration-300`}>
                <i className={`${stat.icon} text-3xl text-white`}></i>
              </div>
              <div className="text-4xl font-black text-white">
                <span className="counter" data-target={stat.number}>0</span>
                <span>{stat.suffix}</span>
              </div>
              <p className="text-sm text-gray-300 font-semibold">{stat.label}</p>
            </div>
          ))}
          </div>
        </div>
      </div>
    </section>
  );
}
